import { FunctionComponent, useCallback, useEffect, useMemo, useState } from "react";
import ReactDOM from "react-dom";
import { useTranslation } from "react-i18next";

import { faCheck, faXmark } from "@fortawesome/pro-regular-svg-icons";

import { classNames } from "@/utils/classNames";
import { useAppDispatch, useAppSelector } from "@/redux/hooks";
import { useIsFeatureActive } from "@/utils/useIsFeatureActive";
import { FeaturesCodes } from "@/pages/Private/pages/Users/enum/codes";
import { Icon } from "@/components/Icon/Icon";
import { Button } from "@/components/Button/Button";
import { ButtonColor, ButtonSize } from "@/components/Button/types";

import {
	useGetNotificationsQuery,
	useMarkAllAsReadMutation,
} from "@/pages/Private/redux/notification/notification.api";
import {
	closePanel,
	notificationSelector,
} from "@/pages/Private/redux/notification/notification.slice";

import { NotificationCategory } from "../enum/notification.enum";
import { useNotificationPanelDeepLink } from "../hooks/useNotificationPanelDeepLink";
import { NotificationQueryParams } from "../schema/notification";
import { NotificationItem } from "./NotificationItem";

type TabValue = "all" | "unread";

export const NotificationPanel: FunctionComponent = () => {
	const { t } = useTranslation();
	const tp = (key: string, opts?: Record<string, unknown>) => t(`notifications.panel.${key}`, opts);

	const dispatch = useAppDispatch();
	const { isPanelOpen, unreadCount } = useAppSelector(notificationSelector);
	const isActive = useIsFeatureActive(FeaturesCodes.NOTIFICATION_CENTER);

	const [currentTab, setCurrentTab] = useState<TabValue>("unread");
	const [category, setCategory] = useState<NotificationCategory | "">("");
	const [page, setPage] = useState(1);
	const limit = 20;

	// A `?panel=notifications` link (e.g. the digest e-mail's "View all notifications") opens it.
	useNotificationPanelDeepLink();

	// Reset state when panel opens
	useEffect(() => {
		if (isPanelOpen) {
			setCurrentTab("unread");
			setCategory("");
			setPage(1);
		}
	}, [isPanelOpen]);

	const queryParams = useMemo<NotificationQueryParams>(() => {
		const params: NotificationQueryParams = { page, limit };

		if (currentTab === "unread") {
			params.isRead = "false";
		}

		if (category) {
			params.category = category;
		}

		return params;
	}, [page, limit, currentTab, category]);

	const { data, isFetching } = useGetNotificationsQuery(queryParams, {
		skip: !isActive || !isPanelOpen,
		refetchOnMountOrArgChange: true,
	});

	const [markAllAsRead, { isLoading: isMarkingAll }] = useMarkAllAsReadMutation();

	const notifications = data?.data ?? [];
	const totalPages = data?.meta?.pageCount ?? 1;

	const handleClose = useCallback(() => {
		dispatch(closePanel());
	}, [dispatch]);

	const handleMarkAllAsRead = useCallback(async () => {
		try {
			await markAllAsRead();
		} catch (e) {
			console.error("Failed to mark all as read:", e);
		}
	}, [markAllAsRead]);

	const handleTabChange = useCallback((tab: TabValue) => {
		setCurrentTab(tab);
		setPage(1);
	}, []);

	const handleCategoryChange = useCallback((cat: NotificationCategory | "") => {
		setCategory(cat);
		setPage(1);
	}, []);

	if (!isActive) {
		return null;
	}

	return ReactDOM.createPortal(
		<>
			{/* Backdrop */}
			<div
				className={classNames(
					"fixed inset-0 bg-black/30 z-[9998] transition-opacity duration-300",
					isPanelOpen ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
				)}
				onClick={handleClose}
			/>

			{/* Panel */}
			<div
				className={classNames(
					"fixed top-0 right-0 h-full w-[420px] max-w-[90vw] bg-white shadow-xl z-[9999] flex flex-col transition-transform duration-300 ease-in-out",
					isPanelOpen ? "translate-x-0" : "translate-x-full"
				)}
			>
				{/* Header */}
				<div className="flex flex-row items-center justify-between px-4 py-3 border-b border-border-light">
					<h2 className="text-base font-semibold text-gray-900">{tp("title")}</h2>
					<div className="flex flex-row items-center gap-2">
						{unreadCount > 0 && (
							<Button
								color={ButtonColor.ACTION_SECONDARY}
								disabled={isMarkingAll}
								image={<Icon className="mr-1" icon={faCheck} />}
								isLoading={isMarkingAll}
								size={ButtonSize.XS}
								title={tp("markAllRead")}
								onClick={handleMarkAllAsRead}
							/>
						)}
						<button
							className="p-1 text-gray-500 hover:text-gray-700 cursor-pointer"
							onClick={handleClose}
						>
							<Icon icon={faXmark} />
						</button>
					</div>
				</div>

				{/* Tabs + Filter */}
				<div className="flex flex-row items-center justify-between px-4 py-2 border-b border-border-light">
					<div className="flex flex-row gap-1">
						{(["all", "unread"] as TabValue[]).map((tab) => (
							<button
								key={tab}
								className={classNames(
									"px-3 py-1 text-sm rounded-lg cursor-pointer transition-colors duration-150",
									currentTab === tab
										? "bg-blue-action text-white font-semibold"
										: "text-gray-600 hover:bg-gray-100"
								)}
								onClick={() => handleTabChange(tab)}
							>
								{tab === "all" ? tp("tabs.all") : tp("tabs.unread")}
							</button>
						))}
					</div>

					{/* Category filter */}
					<select
						className="text-xs border border-border-light rounded-lg px-2 py-1 bg-white text-gray-600 cursor-pointer"
						value={category}
						onChange={(e) => handleCategoryChange(e.target.value as NotificationCategory | "")}
					>
						<option value="">{tp("allCategories")}</option>
						{Object.values(NotificationCategory).map((cat) => (
							<option key={cat} value={cat}>
								{cat.charAt(0) + cat.slice(1).toLowerCase().replace(/_/g, " ")}
							</option>
						))}
					</select>
				</div>

				{/* Notification list */}
				<div className="flex-1 overflow-y-auto bb-scrollbar-darker">
					{isFetching && notifications.length === 0 ? (
						<div className="flex items-center justify-center h-32">
							<span className="text-sm text-gray-400">{tp("loading")}</span>
						</div>
					) : notifications.length === 0 ? (
						<div className="flex flex-col items-center justify-center h-32 gap-1">
							<span className="text-sm text-gray-400">{tp("empty")}</span>
							{currentTab === "unread" && (
								<span className="text-xs text-gray-300">{tp("allCaughtUp")}</span>
							)}
						</div>
					) : (
						notifications.map((notification) => (
							<NotificationItem
								key={notification.id}
								notification={notification}
								onClose={handleClose}
							/>
						))
					)}
				</div>

				{/* Pagination */}
				{totalPages > 1 && (
					<div className="flex flex-row items-center justify-between px-4 py-2 border-t border-border-light">
						<button
							className={classNames(
								"text-xs px-2 py-1 rounded cursor-pointer",
								page <= 1
									? "text-gray-300 cursor-not-allowed"
									: "text-blue-action hover:bg-gray-100"
							)}
							disabled={page <= 1}
							onClick={() => setPage((p) => Math.max(1, p - 1))}
						>
							{tp("previous")}
						</button>
						<span className="text-xs text-gray-500">{tp("pageOf", { page, totalPages })}</span>
						<button
							className={classNames(
								"text-xs px-2 py-1 rounded cursor-pointer",
								page >= totalPages
									? "text-gray-300 cursor-not-allowed"
									: "text-blue-action hover:bg-gray-100"
							)}
							disabled={page >= totalPages}
							onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
						>
							{tp("next")}
						</button>
					</div>
				)}
			</div>
		</>,
		document.body
	);
};
